import React, { useEffect, useState } from "react";
import API_BASE_URL from "../../config/apiConfig";
import { useAuth } from "../../context/AuthProvider";
import WebmasterSidebar from "../../components/WebmasterSidebar";
import ExportScores from "../../components/ExportScores";
import avatar from "../../assets/images/icon-avatar.svg";

const intervalOptions = [
  { label: "Săptămâna curentă", value: "this_week" },
  { label: "Săptămâna trecută", value: "last_week" },
  { label: "Luna curentă", value: "this_month" },
  { label: "Luna trecută", value: "last_month" },
  { label: "Interval custom", value: "custom" },
];

const sortOptions = [
  { label: "Punctaj total (desc)", value: "total_score" },
  { label: "Unități terapie (desc)", value: "therapy_units" },
  { label: "Acoperire program (desc)", value: "coverage_percent" },
  { label: "Penalizări (desc)", value: "penalty_score" },
];

function hexToRGBA(hex: string, opacity: number): string {
  hex = hex.replace("#", "");
  if (hex.length !== 6) return `rgba(200, 200, 200, ${opacity})`;
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

const getStartEndDates = (type: string) => {
  const today = new Date();
  let start: Date;
  let end: Date;

  const getMondayOfWeek = (date: Date) => {
    const clone = new Date(date.getTime());
    const day = clone.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    clone.setDate(clone.getDate() + diff);
    clone.setHours(0, 0, 0, 0);
    return clone;
  };

  if (type === "this_week") {
    start = getMondayOfWeek(today);
    end = new Date(start);
    end.setDate(start.getDate() + 6);
  } else if (type === "last_week") {
    const lastWeek = new Date(today);
    lastWeek.setDate(today.getDate() - 7);
    start = getMondayOfWeek(lastWeek);
    end = new Date(start);
    end.setDate(start.getDate() + 6);
  } else if (type === "this_month") {
    start = new Date(today.getFullYear(), today.getMonth(), 1);
    end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  } else if (type === "last_month") {
    const year =
      today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
    const month = today.getMonth() === 0 ? 11 : today.getMonth() - 1;
    start = new Date(year, month, 1);
    end = new Date(year, month + 1, 0);
  } else {
    start = today;
    end = today;
  }

  const format = (d: Date, time: "start" | "end") => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}${
      time === "start" ? "T00:00:00" : "T23:59:59.999"
    }`;
  };

  return {
    start_time: format(start, "start"),
    end_time: format(end, "end"),
  };
};

interface ScoreEntry {
  count: number;
  score: number;
}

interface TherapistScoreData {
  therapist_id: string;
  entries: Record<string, ScoreEntry>;
  total_score: number;
  therapy_units: number;
  penalty_entries: Record<string, ScoreEntry>;
  penalty_score: number;
  norma_slots: number;
  coverage_percent: number;
  vacation_days: number;
}

interface Therapist {
  user_id: string;
  firstname: string;
  lastname: string;
  therapist_category?: string;
  is_active: boolean;
}

interface Item {
  key: string;
  value: string;
}

interface TherapistCategory {
  key: string;
  value: string;
  priority: string;
  color: string;
}

const TherapistScoreTable: React.FC = () => {
  const { token } = useAuth();
  const [scoreData, setScoreData] = useState<TherapistScoreData[]>([]);
  const [therapists, setTherapists] = useState<Therapist[]>([]);
  const [categories, setCategories] = useState<TherapistCategory[]>([]);
  const [typeMap, setTypeMap] = useState<Record<string, string>>({});
  const [intervalType, setIntervalType] = useState("this_week");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [sortBy, setSortBy] = useState("total_score");
  const [isCollapsed, setIsCollapsed] = useState(
    () => localStorage.getItem("sidebarCollapsed") === "true"
  );

  useEffect(() => {
    if (!token) return;
    const fetchAll = async () => {
      try {
        const [therapistRes, therapyRes, activityRes, catRes] =
          await Promise.all([
            fetch(`${API_BASE_URL}/users/therapists`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            fetch(`${API_BASE_URL}/types/therapy-type`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            fetch(`${API_BASE_URL}/types/activities`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            fetch(`${API_BASE_URL}/types/therapist-categories`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ]);

        const tData = await therapistRes.json();
        const th = await therapyRes.json();
        const ac = await activityRes.json();
        const catData = await catRes.json();

        const map: Record<string, string> = {};
        [...(th.data || []), ...(ac.data || [])].forEach(
          (i: Item) => (map[i.key] = i.value)
        );

        setTherapists(tData || []);
        setCategories(catData.data || []);
        setTypeMap(map);
      } catch (err) {
        console.error("Eroare la încărcarea datelor:", err);
      }
    };
    fetchAll();
  }, [token]);

  const fetchScores = async () => {
    if (!token) return;
    let start_time = "",
      end_time = "";
    if (intervalType === "custom") {
      if (!customStart || !customEnd) return;
      start_time = `${customStart}T00:00:00`;
      end_time = `${customEnd}T23:59:59.999`;
    } else {
      const range = getStartEndDates(intervalType);
      start_time = range.start_time;
      end_time = range.end_time;
    }

    const params = new URLSearchParams({ start_time, end_time });
    const url = `${API_BASE_URL}/score-config/total-by-therapist?${params}`;

    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        console.error("[fetchScores] HTTP", res.status, await res.text());
        return;
      }
      const data = await res.json();
      setScoreData(data || []);
    } catch (err) {
      console.error("[fetchScores] Fetch failed:", err);
    }
  };

  useEffect(() => {
    fetchScores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, intervalType, customStart, customEnd]);

  const getCategoryData = (categoryKey?: string) =>
    categories.find((cat) => cat.key === categoryKey) || {
      priority: "999",
      color: "#ccc",
    };

  const scoreByTherapist: Record<string, TherapistScoreData> = {};
  for (const row of scoreData) {
    scoreByTherapist[row.therapist_id] = row;
  }

    // ─── Statistici globale ───
  const globalStats = React.useMemo(() => {
    const activeIds = new Set(
      therapists.filter((t) => t.is_active).map((t) => t.user_id)
    );
    const rows = scoreData.filter((r) => activeIds.has(r.therapist_id));

    const withNorma = rows.filter((r) => (r.norma_slots || 0) > 0);
    const coverageAvg = withNorma.length
      ? Math.round(
          (withNorma.reduce((s, r) => s + (r.coverage_percent || 0), 0) /
            withNorma.length) * 10
        ) / 10
      : 0;

    return {
      coverageAvg,
      totalTherapists: activeIds.size,
      totalUnits: rows.reduce((s, r) => s + (r.therapy_units || 0), 0),
      totalSlots: rows.reduce((s, r) => s + (r.norma_slots || 0), 0),
      totalScore: Math.round(rows.reduce((s, r) => s + (r.total_score || 0), 0) * 100) / 100,
      totalPenalty: Math.round(rows.reduce((s, r) => s + (r.penalty_score || 0), 0) * 100) / 100,
    };
  }, [scoreData, therapists]);

  const coverageColor = (pct: number) => {
    if (pct >= 75) return "#059669";
    if (pct >= 50) return "#d97706";
    return "#dc2626";
  };

  const filteredTherapists = therapists.filter((t) => t.is_active);

  const getSortValue = (tid: string): number => {
    const row = scoreByTherapist[tid];
    if (!row) return 0;
    switch (sortBy) {
      case "total_score":
        return row.total_score || 0;
      case "therapy_units":
        return row.therapy_units || 0;
      case "coverage_percent":
        return row.coverage_percent || 0;
      case "penalty_score":
        return Math.abs(row.penalty_score || 0);
      default:
        return row.total_score || 0;
    }
  };

  const baseSortedTherapists = [...filteredTherapists].sort((a, b) => {
    const catA = getCategoryData(a.therapist_category);
    const catB = getCategoryData(b.therapist_category);
    return parseInt(catA.priority) - parseInt(catB.priority);
  });

  const withValue = baseSortedTherapists
    .filter((t) => getSortValue(t.user_id) > 0)
    .sort((a, b) => getSortValue(b.user_id) - getSortValue(a.user_id));

  const zeroValue = baseSortedTherapists.filter(
    (t) => getSortValue(t.user_id) === 0
  );

  const orderedTherapists = [...withValue, ...zeroValue];

  const getSelectedRangeISO = () => {
    if (intervalType === "custom") {
      if (!customStart || !customEnd) return null;
      return {
        start_time: `${customStart}T00:00:00`,
        end_time: `${customEnd}T23:59:59.999`,
      };
    }
    return getStartEndDates(intervalType);
  };
  const range = getSelectedRangeISO();

  return (
    <div className={`dashboard-container ${isCollapsed ? "collapsed" : ""}`}>
      <WebmasterSidebar onCollapseChange={setIsCollapsed} />
      <div className="page-content">
        <h1 className="page-title">Punctaje Terapeuți</h1>

        {/* ─── Controls bar ─── */}
        <div className="interval-container">
          <label>Interval: </label>
          <select
            value={intervalType}
            onChange={(e) => setIntervalType(e.target.value)}
          >
            {intervalOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          {intervalType === "custom" && (
            <>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
              />
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
              />
            </>
          )}

          <label style={{ marginLeft: 16 }}>Sortare: </label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            {sortOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          {range && (
            <div style={{ marginLeft: 12 }}>
              <ExportScores
                start_time={range.start_time}
                end_time={range.end_time}
              />
            </div>
          )}
        </div>

                {/* ═══ Acoperire generală ═══ */}
        <div className="global-stats-banner">
          <div className="global-stat-main">
            <div className="global-stat-label">Acoperire program terapeuți (media)</div>
            <div
              className="global-stat-value"
              style={{ color: coverageColor(globalStats.coverageAvg) }}
            >
              {globalStats.coverageAvg}%
            </div>
            <div className="global-stat-detail">
              {globalStats.totalUnits} / {globalStats.totalSlots} sloturi (total)
            </div>
          </div>
          <div className="global-stat-side">
            <div className="global-stat-item">
              <span className="gs-value">{globalStats.totalTherapists}</span>
              <span className="gs-label">terapeuți activi</span>
            </div>
            <div className="global-stat-item">
              <span className="gs-value">{globalStats.totalUnits}</span>
              <span className="gs-label">unități terapie</span>
            </div>
            <div className="global-stat-item">
              <span className="gs-value">{globalStats.totalScore} p</span>
              <span className="gs-label">punctaj total</span>
            </div>
            {globalStats.totalPenalty < 0 && (
              <div className="global-stat-item">
                <span className="gs-value gs-penalty">{globalStats.totalPenalty} p</span>
                <span className="gs-label">penalizări</span>
              </div>
            )}
          </div>
        </div>

        {/* ─── Cards ─── */}
        <div className="therapist-cards-container">
          {orderedTherapists.map((therapist) => {
            const row = scoreByTherapist[therapist.user_id];
            const category = getCategoryData(therapist.therapist_category);
            const bg = hexToRGBA(category.color, 1);

            const totalScore = row?.total_score || 0;
            const therapyUnits = row?.therapy_units || 0;
            const normaSlots = row?.norma_slots || 0;
            const penaltyScore = row?.penalty_score || 0;
            const coveragePercent = row?.coverage_percent || 0;
            const vacationDays = row?.vacation_days || 0;
            const hasEntries = row && Object.keys(row.entries).length > 0;
            const hasPenalties =
              row && Object.keys(row.penalty_entries || {}).length > 0;

            return (
              <div
                key={therapist.user_id}
                className="therapist-card"
                style={{ backgroundColor: bg }}
              >
                {/* ─── Header: doar numele ─── */}
                <h3>
                  <img src={avatar} alt="avatar" className="user-icon" />
                  {therapist.firstname} {therapist.lastname}
                </h3>

                {/* ─── Detaliere puncte ─── */}
                {hasEntries ? (
                  <ul>
                    {Object.entries(row!.entries).map(([key, entry]) => (
                      <li key={key}>
                        {typeMap[key] || key} <em>({entry.count})</em>:{" "}
                        <span>{entry.score} p</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="card-empty">
                    <em>Nu există activități sau terapii efectuate.</em>
                  </p>
                )}

                {/* ─── Penalizări (dacă există) ─── */}
                {hasPenalties && (
                  <div className="penalty-section">
                    <div className="penalty-header">Penalizări:</div>
                    <ul className="penalty-list">
                      {Object.entries(row!.penalty_entries).map(
                        ([key, entry]) => (
                          <li key={key}>
                            {typeMap[key] || key} <em>({entry.count})</em>:{" "}
                            <span className="penalty-value">
                              {entry.score} p
                            </span>
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                )}

                {/* ─── Footer: totaluri + stats ─── */}
                <div className="card-footer">
                  <div className="card-footer-row">
                    <span className="card-footer-label">Total U.T.:</span>
                    <span className="card-footer-value">{therapyUnits}</span>
                  </div>
                  <div className="card-footer-row">
                    <span className="card-footer-label">Total puncte:</span>
                    <span className="card-footer-value card-footer-score">
                      {totalScore} p
                    </span>
                  </div>
                  {hasPenalties && (
                    <div className="card-footer-row card-footer-penalty">
                      <span className="card-footer-label">Penalizare:</span>
                      <span className="card-footer-value penalty-value">
                        {penaltyScore} p
                      </span>
                    </div>
                  )}
                  <div className="card-footer-row">
                    <span className="card-footer-label">Acoperire:</span>
                    <span className="card-footer-value">
                      {coveragePercent}%
                      <span className="card-footer-detail">
                        {" "}({therapyUnits}/{normaSlots})
                      </span>
                    </span>
                  </div>
                  {vacationDays > 0 && (
                    <div className="card-footer-row">
                      <span className="card-footer-label">Concediu:</span>
                      <span className="card-footer-value">
                        {vacationDays} {vacationDays === 1 ? "zi" : "zile"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TherapistScoreTable;